import React, { useEffect, useRef, useState } from 'react'
import { Progress, Textarea, Typography } from '@material-tailwind/react';
import Resumable from 'resumablejs';
import JSZip from 'jszip';
import Nav from "../components/Nav/Nav"
import SelectionButton from '../components/Index/SelectionButton';
import SettingsModal from '../components/Index/SettingsModal';
import PostUpload from '../components/Index/PostUpload';
import { InformationIcon, SuccessIcon, UploadIcon, WarningIcon } from '../components/Icons';
import AlertPopup from '../components/Index/AlertPopup';
import UploadCloud from '../components/AnimatedIcons/UploadCloud';
import Footer from '../components/Footer/Footer';
import UploadCaptcha from '../components/auth/UploadCaptcha';

export default function Index() {
  const [uploadSessionId, setUploadSessionId] = useState(null);
  const [maxFileSize, setMaxFileSize] = useState(2 * 1024 * 1024 * 1024);
  const [maxTextLength, setMaxTextLength] = useState(1000000);
  const [uploadType, setUploadType] = useState(localStorage.getItem("uploadType") || "files")
  const [fileArray, setFileArray] = useState([]);
  const [zippingFolders, setZippingFolders] = useState([]);
  const [cancellationTokens, setCancellationTokens] = useState({});
  const [textInput, setTextInput] = useState(localStorage.getItem("textInput") || "");
  const [isDragOver, setIsDragOver] = useState(false);
  const [alert, setAlert] = useState({ display: false })
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [uploadSettings, setIsUploadSettings] = useState({})
  const [uploadLoadingDots, setUploadLoadingDots] = useState("")
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [successfulUploads, setSuccessfulUploads] = useState(0);
  const [uploadErrors, setUploadErrors] = useState(0);
  const [resetFlag, setResetFlag] = useState(false);
  const [link, setLink] = useState(null)
  const [isHoveredOverUploadBox, setIsHoveredOverUploadBox] = useState(false);
  const [isCaptchaShown, setIsCatpchaShown] = useState(false);
  const alertPopupRef = useRef(null);
  const fileInputRef = useRef(null);
  const resumableRef = useRef(null);
  const fullUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}${window.location.pathname}${window.location.search}${window.location.hash}`;

  // useEffect(() => console.log(fileArray), [fileArray]);

  useEffect(() => {
    getMaxUpload();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setUploadLoadingDots(prevDots => {
        if (prevDots.length < 3) {
          return prevDots + '.';
        } else {
          return '';
        }
      });
    }, 750);

    return () => clearInterval(interval);
  }, []);

  useEffect(saveToLocalStorage, [uploadType, textInput])

  useEffect(() => {
    if (totalFiles > 0 && (successfulUploads + uploadErrors === totalFiles)) {
      if (successfulUploads === totalFiles && uploadErrors === 0) {
        setResetFlag(true);
        setUploadStatus("uploaded");
        setTimeout(() => {
          setResetFlag(false);
        }, 1000)
      } else {
        handleResetUpload({ 
          defaultsTo: false, 
          resetResumableRef: true,
          resetUploadProgress: true,
          resetUploadStatus: true,
          resetUploadSessionId: true,
        });
      }

      setTotalFiles(0);
      setSuccessfulUploads(0);
      setUploadErrors(0);
      clearLocalStorage();
    } 
    // TODO: Handle cases where condition is false, probably need to cancel upload server side since it's successful = delete server side
  }, [totalFiles, successfulUploads, uploadErrors])

  function setupResumable(resumableUploadSessionId, token) {
    const onFileAdded = () => {
      setTotalFiles(prevTotal => prevTotal + 1);
    };

    const onFileSuccess = (_, message) => {
      setLink(fullUrl + JSON.parse(message).uploadId);
      setSuccessfulUploads(prevSuccesses => prevSuccesses + 1);
    };

    const onFileError = (_, error) => {
      setUploadErrors(prevErrors => prevErrors + 1);
      const message = JSON.parse(error).message;
      let title, description;
      if (message.includes("|")) {
        [title, description] = message.split("|")
      }
      showAlertPopup(<WarningIcon />, "red", title || "An error has occured", description || message, 2500);
      resumableRef.current.cancel();
    };

    const onProgress = () => {
      const progress = Math.floor(resumableRef.current.progress() * 100);
      setUploadProgress(progress);
    };

    resumableRef.current = new Resumable({
      target: `https://uploads.jellyo.net/api/upload/${uploadType}`,
      chunkSize: 10 * 1024 * 1024,
      testChunks: false,
      maxFileSize: maxFileSize,
      fileParameterName: uploadType,
      query: { 
        uploadSessionId: resumableUploadSessionId, 
        uploadSettings: JSON.stringify(uploadSettings),
        captchaToken: token
      },
      withCredentials: true,
      permanentErrors: [400, 401, 402, 403, 404, 405, 406, 407, 408, 409, 429, 500, 501],
      maxChunkRetries: 10
    });

    resumableRef.current.on('fileAdded', onFileAdded);
    resumableRef.current.on('fileSuccess', onFileSuccess);
    resumableRef.current.on('fileError', onFileError);
    resumableRef.current.on('progress', onProgress);
  }

  async function getUploadSessionId() {
    const response = await fetch("https://uploads.jellyo.net/api/upload/uploadsessionid", { credentials: "include" });
    const responseData = await response.json();
    return responseData.uploadSessionId;
  }

  async function getMaxUpload() {
    const response = await fetch("https://uploads.jellyo.net/api/upload/maxupload", { credentials: "include"});
    const responseData = await response.json();
    setMaxFileSize(responseData.maxFileSize);
    setMaxTextLength(responseData.maxTextLength);
  }

  function convertFileSize(bytes, round) {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    if (i === 0) return `${bytes} ${units[i]}`; // No decimal for bytes
    return `${(bytes / Math.pow(1024, i)).toFixed(round || 0)} ${units[i]}`;
  }

  function formatNumber(num) {
    if (num >= 1000000000) {
       return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (num >= 1000000) {
       return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
       return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num;
  }

  function handleChangeUploadType(type) {
    setUploadType(type);
  }

  function playUploadCloudAnimation() {
    setIsHoveredOverUploadBox(true);
  };

  function stopUploadCloudAnimation() {
    setIsHoveredOverUploadBox(false);
  }

  const generateUniqueId = (() => {
    let counter = 0;
    return () => `${Date.now()}-${counter++}`;
  })();
  
  function addFiles(newFiles, ids = []) {
    fileInputRef.current.value = "";
  
    setFileArray(prevFiles => {
      const updatedFiles = [...prevFiles];
      newFiles.forEach((newFile, index) => {
        const id = ids[index] || generateUniqueId();
        const existingIndex = updatedFiles.findIndex(file => file.id === id);
  
        if (existingIndex > -1) {
          // Replace the existing file
          updatedFiles[existingIndex] = { id, file: newFile };
        } else {
          // Only add if not cancelled
          if (!cancellationTokens[id] || !cancellationTokens[id].cancelled) {
            updatedFiles.push({ id, file: newFile });
          }
        }
      });
      return updatedFiles;
    });
  }
  
  function removeFile(fileId) {
    if (cancellationTokens[fileId]) {
      cancellationTokens[fileId].cancelled = true;
    }
  
    setFileArray(prevFiles => prevFiles.filter(file => file.id !== fileId));
  
    setZippingFolders(prev => {
      const updated = prev.filter(id => id !== fileId);
      return updated;
    });
  }
  
  function handleDragOver(e) {
    e.preventDefault();
    setIsDragOver(true);
    playUploadCloudAnimation();
  }
  
  function handleDragLeave() {
    setIsDragOver(false);
    stopUploadCloudAnimation();
  }
  
  async function handleDrop(e, zipFlag = true) {
    e.preventDefault();
    stopUploadCloudAnimation();
    setIsDragOver(false);

    const items = Array.from(e.dataTransfer.items);

    const hasFiles = e.dataTransfer.files.length > 0;
    const hasFolders = items.some(item => item.webkitGetAsEntry()?.isDirectory);

    if (hasFiles && !hasFolders) {
      const files = Array.from(e.dataTransfer.files);
      addFiles(files);
    } else {
      const { files, directories } = await getAllFilesAndDirectories(items);

      if (files.length > 0) {
        addFiles(files);
      }

      if (directories.length > 0) {
        if (zipFlag) {
          for (const dirEntry of directories) {
            const uniqueId = generateUniqueId();
            const cancellationToken = { cancelled: false };
            setCancellationTokens(prevTokens => ({ ...prevTokens, [uniqueId]: cancellationToken }));
            setZippingFolders(prev => [...prev, uniqueId]);
    
            // Pass the uniqueId to zipDirectoryWithProgress
            zipDirectoryWithProgress(dirEntry, uniqueId, cancellationToken);
          }
        } else {
          for (const dirEntry of directories) {
            const dirFiles = await readDirectoryContentsIncrementally(dirEntry);
            addFiles(dirFiles);
          }
        }
      }
    }
  }
  
  async function getAllFilesAndDirectories(items) {
    const allFiles = [];
    const allDirectories = [];
  
    return Promise.all(items.map(async (item) => {
      const entry = item.webkitGetAsEntry();
      if (entry) {
        if (entry.isFile) {
          return readEntry(entry).then(file => allFiles.push(file));
        } else if (entry.isDirectory) {
          allDirectories.push(entry);
        }
      }
      return Promise.resolve();
    })).then(() => ({ files: allFiles, directories: allDirectories }));
  }
  
  function readEntry(entry) {
    return new Promise((resolve) => {
      entry.file(resolve);
    });
  }
  
  async function zipDirectoryWithProgress(directory, uniqueId, cancellationToken) {
    try {
      const zip = new JSZip();
      const baseFileName = `${directory.name}.zip`;
      const inProgressFileName = `${baseFileName} (zipping in progress)`;
  
      const addFileToZip = async (entry, path = '') => {
        if (cancellationToken.cancelled) return;
  
        if (entry.isFile) {
          const file = await readEntry(entry);
          if (cancellationToken.cancelled) return;
  
          return new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = async (e) => {
              if (cancellationToken.cancelled) {
                resolve();
                return;
              }
              zip.file(path + file.name, e.target.result);
              await updateZipFile(inProgressFileName);
              resolve();
            };
            reader.readAsArrayBuffer(file);
          });
        } else if (entry.isDirectory) {
          const dirReader = entry.createReader();
          const entries = await new Promise(resolve => dirReader.readEntries(resolve));
  
          for (const subEntry of entries) {
            if (cancellationToken.cancelled) break;
            await addFileToZip(subEntry, path + entry.name + '/');
          }
        }
      };
  
      const updateZipFile = async (fileName) => {
        if (cancellationToken.cancelled) return;
  
        const zipBlob = await zip.generateAsync({ type: 'blob', compression: "STORE", streamFiles: true });
        if (cancellationToken.cancelled) return;
  
        const zipFile = new File([zipBlob], fileName, { type: 'application/zip' });
        addFiles([zipFile], [uniqueId]);
      };
  
      await addFileToZip(directory);
      if (cancellationToken.cancelled) return;
  
      // Final zip creation with the original filename
      await updateZipFile(baseFileName);
    } finally {
      // Clean up cancellation tokens
      setCancellationTokens(prevTokens => {
        const newTokens = { ...prevTokens };
        delete newTokens[uniqueId];
        return newTokens;
      });
  
      // Remove from zippingFolders using uniqueId
      setZippingFolders(prev => {
        const updated = prev.filter(id => id !== uniqueId);
        return updated;
      });
  
      if (cancellationToken.cancelled) {
        // Remove the partial zip file from fileArray using uniqueId
        setFileArray(prevFiles => prevFiles.filter(file => file.id !== uniqueId));
      }
    }
  }
  
  function handleTextInput(e) {
    if (e.target.value.length > maxTextLength) {
      showAlertPopup(<WarningIcon />, "red", "Max text length exceeded!", `Max text length is ${formatNumber(maxTextLength)} characters`, 3000)
      return;
    }

    setTextInput(e.target.value);
  }
  
  function handleOpenSettingsModal() {
    setIsSettingsModalOpen(!isSettingsModalOpen);
  }

  function showAlertPopup(icon, color, title, description, timeout) {
    setAlert({ display: true, color, icon, title, description});

    if (alertPopupRef.current) clearTimeout(alertPopupRef.current);

    alertPopupRef.current = setTimeout(() => {
      setAlert({ display: false })
    }, timeout);
  }

  function showCaptcha() {
    setIsCatpchaShown(true);
  }

  async function upload(token) {
    if (zippingFolders.length > 0) {
      showAlertPopup(<WarningIcon />, "red", "Zipping in progress", "Please wait for the folders to finish zipping before uploading.", 2500);
      return;
    }

    if (!uploadSessionId) {
      const resumableUploadSessionId = await getUploadSessionId();
      setUploadSessionId(resumableUploadSessionId);
      setupResumable(resumableUploadSessionId, token);
    }
    
    if (!resumableRef.current) {
      showAlertPopup(<WarningIcon />, "red", "Invalid Upload", "An error has occurred. Please refresh the page and try again", 2500);
      return;
    }


    switch (uploadType) {
      case "files":
        if (fileArray.length > 0) {
          setUploadStatus("uploading");
          await resumableRef.current.addFiles(fileArray.map(file => file.file));
        } else {
          showAlertPopup(<WarningIcon />, "red", "Invalid Upload", "You must upload at least 1 file!", 2500);
          return;
        }
        break
      case "text":
        if (textInput.length > maxTextLength) {
          showAlertPopup(<WarningIcon />, "red", "Max text length exceeded!", `Max text length is ${formatNumber(maxTextLength)} characters`, 3000)
          return;
        }
        if (textInput.length > 0) {
          const textBlob = new Blob([textInput], { type: 'text/plain' });
          const textFile = new File([textBlob], 'toUpload.txt', { type: 'text/plain' });
          await resumableRef.current.addFile(textFile);
        } else {
          showAlertPopup(<WarningIcon />, "red", "Invalid Upload", "You must upload at least 1 character!", 2500)
          return;
        }
        break
    }

    await new Promise(r => setTimeout(r, 500));

    resumableRef.current.upload();
  }

  function handlePauseUpload() {
    resumableRef.current.pause();
    setUploadStatus("pausedUploading");
    showAlertPopup(<InformationIcon />, "amber", "Upload Paused", "Upload is now paused", 2500);
  }

  function handleResumeUpload() {
    resumableRef.current.upload();
    setUploadStatus("uploading");
    showAlertPopup(<InformationIcon />, "green", "Upload Resumed", "Upload is now resumed", 2500);
  }

  async function handleCancelUpload() {
    resumableRef.current.cancel();
    handleResetUpload({ 
      defaultsTo: false, 
      resetResumableRef: true,
      resetUploadProgress: true,
      resetUploadStatus: true,
      resetUploadSessionId: true,
    });

    const response = await fetch("https://uploads.jellyo.net/api/upload/cancel", {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        uploadSessionId
      })
    })

    const responseData = await response.json();

    if (responseData.status === "error") {
      showAlertPopup(<WarningIcon />, "red", "Cancel Failed", responseData.message || "An unexpected error occurred", 5000);
    } else {
      showAlertPopup(<SuccessIcon />, "green", "Cancel Successful", "Upload cancelled successfully", 2500);
    }
  }

  async function handleResetUpload(options = {}) {
    // Determine the default setting based on 'defaultsTo' option
    const defaultsTo = options.defaultsTo ?? true; // Default to true if not specified
  
    // Define the default behavior based on the 'defaultsTo' value
    const defaults = {
      resetResumableRef: defaultsTo,
      resetUploadProgress: defaultsTo,
      resetUploadStatus: defaultsTo,
      resetTextInput: defaultsTo,
      resetFileArray: defaultsTo,
      resetFileInput: defaultsTo,
      resetLocalStorage: defaultsTo,
      resetUploadSessionId: defaultsTo,
    };
  
    // Remove the 'defaultsTo' key to avoid conflicts
    const { defaultsTo: _, ...resetOptions } = options;
    const settings = { ...defaults, ...resetOptions };
  
    if (uploadStatus === "idle") return;
  
    // Conditional resets based on the settings object
    if (settings.resetResumableRef) {
      resumableRef.current = null;
      setTotalFiles(0);
      setSuccessfulUploads(0);
      setUploadErrors(0);
    }
    if (settings.resetUploadProgress) {
      setUploadProgress(0);
    }
    if (settings.resetUploadStatus) {
      setUploadStatus("idle");
    }
    if (settings.resetTextInput) {
      setTextInput("");
    }
    if (settings.resetFileArray) {
      setFileArray(() => []);
    }
    if (settings.resetFileInput && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (settings.resetLocalStorage) {
      clearLocalStorage();
    }
    if (settings.resetUploadSessionId) {
      setUploadSessionId(null);
    }
  }

  function saveToLocalStorage() {
    localStorage.setItem("uploadType", uploadType);
    localStorage.setItem("textInput", textInput);
  }

  function clearLocalStorage() {
    localStorage.removeItem("uploadType");
    localStorage.removeItem("textInput");
  }

  return (
      <div className="overflow-hidden h-dvh w-dvw bg-background-img bg-cover bg-repeat flex flex-col justify-center items-center">
        <Nav resetUpload={handleResetUpload} disableLogoClick={uploadStatus === "uploading"} />
        <AlertPopup display={alert.display} icon={alert.icon} color={alert.color} title={alert.title} description={alert.description} />
        <div className={`${uploadStatus === "uploaded" ? "absolute -translate-x-[210%] opacity-0s" : "opacity-100"} max-w-[50rem] w-full min-h-[30rem] h-max m-4 mt-8 bg-offwhite rounded-xl shadow-all flex flex-col transition-transform duration-700`}>
          <div className="p-4 border-0 border-solid border-b-2 border-gray-200 flex gap-4 text-center text-xs">
            <SelectionButton cb={handleChangeUploadType} label="FILES" active={uploadType === "files"} />
            <SelectionButton cb={handleChangeUploadType} label="TEXT" active={uploadType === "text"} />
          </div>

          <div className='m-4 mb-1 text-center'>
            <h1 className='mb-1 text-xl md:text-3xl text-gray-800 font-medium'>Welcome to Jellyo Uploads!</h1>
            <p className={`${uploadType === "files" ? "" : "hidden"} px-4 text-sm md:text-base text-gray-600`}>Upload as many files of up to {convertFileSize(maxFileSize)} and get a link to share with others</p>
            <p className={`${uploadType === "text" ? "" : "hidden"} px-4 text-sm md:text-base text-gray-600`}>Upload text of up to {formatNumber(maxTextLength)} characters and get a link to share with others</p>
          </div>

          <div
            onClick={() => {
              fileInputRef.current.click();
              playUploadCloudAnimation();
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onMouseOver={playUploadCloudAnimation}
            onMouseLeave={stopUploadCloudAnimation}
            onDrop={handleDrop}
            className={`${uploadType === "files" ? "" : "hidden"} ${!isDragOver ? "border-dashed" : "border-solid bg-bonewhite" } ${fileArray.length === 0 ? "hover:bg-bonewhite items-center" : ""} mx-5 my-3 h-56 border-2 border-black rounded-lg flex justify-center relative cursor-pointer transition-all`}
          >
            <input
              ref={fileInputRef}
              id='files'
              type="file"
              multiple
              onChange={e => addFiles(Array.from(e.target.files))}
              className='w-full h-full cursor-pointer hidden'
            />
            <div className={`${fileArray.length > 0 ? "hidden" : ""} absolute -translate-y-3 px-3 h-10 w-full pointer-events-none flex flex-col justify-center items-center text-center`}>
              <UploadCloud isHovered={isHoveredOverUploadBox} scale="1.4" />
              <div className='w-[94%] absolute translate-y-14 flex flex-col'>
                <span className='text-xl text-gray-900'>Click or drag files here to upload</span>
                <span className='text-sm text-gray-500 mt-2'>Note: Uploaded folders will be zipped. It is advised not to upload large folders as zipping will take up a long time</span>
              </div>
            </div>
            <div className='w-full'>
              <ul className='w-full h-full overflow-auto'>
                {fileArray.map((file, index) => (
                  <li key={file.id} className={`${index % 2 === 0 ? "bg-gray-200" : ""} h-min w-full p-3 flex items-center justify-between`}>
                    <span className="w-1/2 overflow-hidden whitespace-nowrap text-ellipsis">{file.file.name}</span>
                    <span className='w-1/2 whitespace-nowrap ms-6'>{convertFileSize(file.file.size, 2)}</span>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        removeFile(file.id);
                      }}
                      className='bg-red-400 text-offwhite px-2 py-1 rounded-lg hover:scale-105 transition-all'
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className={`${uploadType === "text" ? "" : "hidden"} mx-5 my-3 h-56 textarea-parent-div`}>
            <Textarea value={textInput} onChange={handleTextInput} label='Write your text here' color='blue-gray' className='h-full text-xl' maxLength={maxTextLength} />
          </div>

          <div className='m-5 mt-3 h-12 text-center flex gap-2 items-center justify-center text-offwhite'>
            <div onClick={showCaptcha} className={`${uploadStatus !== "idle" ? "hidden" : ""} h-full w-10/12 sm:w-11/12 bg-gray-900 rounded-lg cursor-pointer flex justify-center items-center hover:bg-black hover:shadow-lg active:scale-105 transition-all`}>
              <span className='text-sm me-2 leading-normal'>UPLOAD</span>
              <UploadIcon />
            </div>
            <div className={`${uploadStatus === "idle" ? "hidden" : ""} w-full`}>
              <div className="mb-1 flex items-center justify-between gap-4">
                <div className='flex gap-2'>
                  <Typography color={uploadProgress !== 100 ? `blue-gray` : "green"} variant="h6" className='w-[5.5rem] text-start'>
                    {uploadProgress !== 100 ? `${uploadStatus.includes("paused") ? "Paused" : "Uploading"}${uploadLoadingDots}` : "Completed"}
                  </Typography>
                  <Typography color={uploadProgress !== 100 ? `blue-gray` : "green"} variant="h6">
                    {uploadProgress !== 100 ? `${uploadProgress}%` : ""}
                  </Typography>
                </div>
                <div className='flex gap-3'>
                  <i onClick={handleResumeUpload} className={`${uploadStatus === "pausedUploading" ? "" : "hidden"} ${uploadProgress !== 100 ? "" : "invisible pointer-events-none"} bx bx-play text-gray-900 rounded-full text-3xl !leading-none cursor-pointer transition-colors`}></i>
                  <i onClick={handlePauseUpload} className={`${uploadStatus === "pausedUploading" ? "hidden" : ""} ${uploadProgress !== 100 ? "" : "invisible pointer-events-none"} bx bx-pause text-gray-900 rounded-full text-3xl !leading-none cursor-pointer transition-colors`}></i>
                  <i onClick={handleCancelUpload} className={`${uploadProgress !== 100 ? "" : "invisible pointer-events-none"} bx bx-stop text-gray-900 rounded-full text-3xl !leading-none cursor-pointer transition-colors`}></i>
                </div>
              </div>
              <Progress 
                value={uploadProgress}
                color={uploadProgress !== 100 ? "gray" : "green"}
                size="lg"
              />
            </div>
            <div onClick={handleOpenSettingsModal} className={`${uploadStatus !== "idle" ? "hidden" : ""} group h-full w-2/12 sm:w-1/12 bg-gray-900 rounded-lg cursor-pointer flex justify-center items-center hover:bg-black hover:shadow-lg active:scale-105 transition-all`}>
              <i className='bx bx-cog text-xl group-hover:animate-jello'></i>
            </div>
          </div>

          <SettingsModal resetFlag={resetFlag} updateSettings={setIsUploadSettings} open={isSettingsModalOpen} handler={handleOpenSettingsModal} />

        </div>
        <PostUpload resetUpload={handleResetUpload} display={uploadStatus === "uploaded"} link={link} />
        <UploadCaptcha isOpen={isCaptchaShown} setIsOpen={setIsCatpchaShown} cb={upload} />
        <Footer />
      </div>
  )
}

